'use client';

import React, { type ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useStickyNav } from './StickyNavContext';

// Unpinned, the header is parked one header height above the viewport rather than far
// off-screen. Sticky never lets it rise above `top`, so animating `top` between the two slides it
// from wherever it is: without that, scrolling back up within the first header-height of the
// page snapped the whole header in at once, and scrolling down there snapped it out (MPH-1224).
const UNMEASURED_PARKED_TOP = '-9999px';
const SLIDE_TRANSITION = '0.35s ease-out';

const Wrapper = styled.div<{ $stickyActive: boolean; $parkedTop: string; $isHidden: boolean; $animate: boolean }>`
  @media (max-width: 767px) {
    position: sticky;
    top: ${({ $stickyActive, $parkedTop }) => ($stickyActive ? '0' : $parkedTop)};
    z-index: 11;
    transform: translateY(${({ $isHidden }) => ($isHidden ? '-100%' : '0')});
    transition: ${({ $animate }) =>
      $animate ? `top ${SLIDE_TRANSITION}, transform ${SLIDE_TRANSITION}` : `top ${SLIDE_TRANSITION}`};

    body:has(.map-layout) & {
      z-index: 2;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const StickyNavHeader = ({ children }: { children: ReactNode }) => {
  const { isOut, scrollDirection } = useStickyNav();
  const wasRevealed = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (isOut && scrollDirection === 'up') wasRevealed.current = true;
    if (!isOut) wasRevealed.current = false;
  }, [isOut, scrollDirection]);

  // The header is not always 64px (two rows for BCOM, the app's standalone CSW), so park it by
  // its own measured height.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || typeof ResizeObserver === 'undefined') return () => {};
    const measure = () => setHeight(wrapper.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, []);

  const slidingIn = isOut && scrollDirection === 'up';
  const slidingOut = wasRevealed.current && isOut && scrollDirection === 'down';

  return (
    <Wrapper
      ref={wrapperRef}
      $stickyActive={isOut || scrollDirection === 'up'}
      $parkedTop={height > 0 ? `-${height}px` : UNMEASURED_PARKED_TOP}
      $isHidden={isOut && scrollDirection === 'down'}
      $animate={slidingIn || slidingOut}
    >
      {children}
    </Wrapper>
  );
};

export default StickyNavHeader;
